import { useParams } from 'react-router-dom'
import { parseAsString, useQueryState } from 'nuqs'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import useQueryGetPartnerPlayers from '@/modules/manage-player/api/hooks/useQueryGetPartnerPlayers'
import useQueryGetPartnerById from '@/modules/manage-player/api/hooks/useQueryGetPartnerById'
import useQueryGetPartnerPlayersSummary from '@/modules/manage-player/api/hooks/useQueryGetPartnerPlayersSummary'
import PartnerPlayersListing from '@/modules/manage-player/components/PartnerPlayers/components/PartnerPlayersListing'

function PartnerPlayersPage() {
	const { id: partnerId } = useParams<{ id: string }>()
	const { pageSize, currentPage } = useTablePagination()
	const { dateFilter, setDateFilter } = useDateFilter()

	const [phone, setPhone] = useQueryState(
		'phone',
		parseAsString.withDefault(''),
	)

	// Fetch partner info
	const { data: partnerData } = useQueryGetPartnerById(partnerId || '')

	// Fetch partner players (paginated data only)
	const payload = {
		skip: (currentPage - 1) * pageSize,
		take: pageSize,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
		phone: phone || undefined,
	}

	const { data, isLoading } = useQueryGetPartnerPlayers(
		partnerId || '',
		payload,
	)

	// Fetch summary separately (only refetches on date/phone filter changes, NOT pagination)
	const summaryPayload = {
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
		phone: phone || undefined,
	}

	const { data: summaryData, isLoading: isSummaryLoading } =
		useQueryGetPartnerPlayersSummary(partnerId || '', summaryPayload)

	const handleResetFilters = () => {
		setPhone('')
		setDateFilter({
			startDate: null,
			endDate: null,
		})
	}

	if (!partnerId) {
		return (
			<PageContainer>
				<div className="flex h-64 items-center justify-center">
					<p className="text-muted-foreground">ไม่พบข้อมูลพาร์ทเนอร์</p>
				</div>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<PartnerPlayersListing
				partnerId={partnerId}
				partnerNickname={partnerData?.nickname}
				partnerIsActive={partnerData?.isActive}
				dateFilter={dateFilter}
				data={data?.data || []}
				total={data?.total || 0}
				isLoading={isLoading}
				summary={summaryData?.summary}
				summaryTotalPlayers={summaryData?.totalPlayers}
				isSummaryLoading={isSummaryLoading}
				phone={phone || ''}
				onPhoneChange={setPhone}
				onResetFilters={handleResetFilters}
			/>
		</PageContainer>
	)
}

const GuardedPartnerPlayersPage = withUserPermission(PartnerPlayersPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_PLAYER],
})

export default GuardedPartnerPlayersPage
